import uuid from 'uuid';
import clone from 'lodash-es/clone';
import without from 'lodash-es/without';

import { fabric } from 'fabric';
import find from 'lodash-es/find';
import max from 'lodash-es/max';
import set from 'lodash-es/set';

import ImageAsync from './ImageAsync';
import LudusChart from './LudusChart';
import LudusCircle from './LudusCircle';
import LudusCode from './LudusCode';
import LudusGuideline from './LudusGuideline';
import LudusIframe from './LudusIframe';
import LudusImage from './LudusImage';
import LudusLine from './LudusLine';
import LudusLottie from './LudusLottie';
import LudusPath from './LudusPath';
import LudusBrush from './LudusBrush';
import LudusBrushPath from './LudusBrushPath';
import LudusPathGroup from './LudusPathGroup';
import LudusRect from './LudusRect';
import LudusRich from './LudusRich';
import LudusSymbol from './LudusSymbol';
import LudusText from './LudusText';
import variables from '../variables';
import throttle from 'lodash-es/throttle';

// const M_SQRT1_2 = 0.7071067811865476;
const multiplyMatrices = fabric.util.multiplyTransformMatrices;
const transformPoint = fabric.util.transformPoint;
const degreesToRadians = fabric.util.degreesToRadians;
const invertTransform = fabric.util.invertTransform;

fabric.LudusText = LudusText;
fabric.LudusRect = LudusRect;
fabric.LudusCircle = LudusCircle;
fabric.LudusLine = LudusLine;
fabric.LudusGuideline = LudusGuideline;
fabric.LudusSymbol = LudusSymbol;
fabric.LudusRich = LudusRich;
fabric.LudusIframe = LudusIframe;
fabric.LudusImage = LudusImage;
fabric.LudusPathGroup = LudusPathGroup;
fabric.LudusPath = LudusPath;
fabric.LudusLottie = LudusLottie;
fabric.LudusCode = LudusCode;
fabric.LudusChart = LudusChart;
fabric.ImageAsync = ImageAsync;
fabric.LudusBrush = LudusBrush;
fabric.LudusBrushPath = LudusBrushPath;

// See https://github.com/kangax/fabric.js/issues/4136 - needed to migrate 1.x path-group to 2.0 groups
fabric.PathGroup = {};
fabric.PathGroup.fromObject = (obj, callback) => {
  const object = obj;
  const originalPaths = object.paths;
  delete object.paths;
  if (typeof originalPaths === 'string') {
    fabric.loadSVGFromString(originalPaths, elements => {
      const pathUrl = originalPaths;
      const group = fabric.util.groupSVGElements(elements, object, pathUrl);
      group.type = 'group';
      object.paths = originalPaths;
      callback(group);
    });
  } else {
    fabric.util.enlivenObjects(originalPaths, enlivenedObjects => {
      enlivenedObjects.forEach(item => {
        item._removeTransformMatrix();
      });
      const group = new fabric.Group(enlivenedObjects, object);
      group.type = 'group';
      object.paths = originalPaths;
      callback(group);
    });
  }
};

// CUSTOMIZE FABRIC TO SAVE BBOX OF SVGs
fabric.loadSVGFromURL = function(url, callback, reviver, options) {
  url = url.replace(/^\n\s*/, '').trim();
  fabric.util.request(url, {
    method: 'get',
    onComplete: onComplete,
  });

  function onComplete(r) {
    var xml = r.responseXML;
    if (
      xml &&
      !xml.documentElement &&
      fabric.window.ActiveXObject &&
      r.responseText
    ) {
      xml = new ActiveXObject('Microsoft.XMLDOM'); // eslint-disable-line no-undef
      xml.async = 'false';
      // IE chokes on DOCTYPE
      xml.loadXML(
        r.responseText.replace(/<!DOCTYPE[\s\S]*?(\[[\s\S]*\])*?>/i, ''),
      );
    }
    if (!xml || !xml.documentElement) {
      callback && callback(null);
      return false;
    }
    const svg = xml.documentElement;
    document.body.appendChild(svg);
    const bbox = svg.getBBox();
    const width = parseFloat(svg.getAttribute('width'));
    const vbox = svg.getAttribute('viewBox');
    const scale = vbox ? width / vbox.split(' ')[2] : 1;

    // Earlier in the process, we scale the dom svg element to get enough pixel to apply fabric filter with decent resolution
    // But the svg.getBbox() uses the viewbox size only, so we must scale it to match the actual size of the object
    // This ensure "makeEditable" function ungroup the svg at the correct position/scale.
    const upscaledBbox = {
      x: bbox.x * scale,
      y: bbox.y * scale,
      width: bbox.width * scale,
      height: bbox.height * scale,
    };
    fabric.parseSVGDocument(
      svg,
      function(results, _options, elements, allElements) {
        document.body.removeChild(svg);
        callback &&
          callback(
            results,
            {
              ..._options,
              bbox: upscaledBbox,
            },
            elements,
            allElements,
          );
      },
      reviver,
      options,
    );
  }
};

fabric.reNonWord = /[ \n"=.,;!?\t\xA0\u1680\u180e\u2000\u200a\u202f\u205f\u3000-]/;

fabric.Text.prototype._styleProperties = [
  'stroke',
  'strokeWidth',
  'fill',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'underline',
  'overline',
  'linethrough',
  'textBackgroundColor',
  'styleCase',
  'originalChar',
];
fabric.ActiveSelection.prototype.lockSkewingX = true;
fabric.ActiveSelection.prototype.lockSkewingY = true;

fabric.Object.prototype.applyStrokeStyle = function() {
  const strokeStyleObject = find(variables.strokeStyles, [
    'name',
    this.strokeStyle,
  ]);
  this.strokeDashArray = strokeStyleObject.values
    .split(',')
    .map(item => eval(item.replace('s', this.strokeWidth))); // eslint-disable-line no-eval
};

Object.defineProperty(fabric.Object.prototype, 'rendered', {
  get() {
    return this._rendered;
  },
  set(value) {
    this._rendered = value;
    if (this.group) {
      this.group.rendered = value;
    }
  },
});

Object.defineProperty(fabric.Object.prototype, 'strokeStyle', {
  get() {
    if (
      this.strokeWidth &&
      this.stroke !== 'transparent' &&
      this._strokeStyle
    ) {
      return this._strokeStyle;
    }
    return 'solid';
  },
  set(value) {
    if (value !== this._strokeStyle) {
      this._strokeStyle = value;
      this.applyStrokeStyle();
    }
  },
});

Object.defineProperty(fabric.Object.prototype, 'borderColor', {
  get() {
    return this.canvas?.editingSymbol
      ? variables.colors.purple
      : this._borderColor;
  },
  set(value) {
    if (value !== this._borderColor) {
      this._borderColor = value;
    }
  },
});

Object.defineProperty(fabric.Object.prototype, 'lockMovementX', {
  get() {
    return this.canvas?.editingSymbol ? true : this._lockMovementX;
  },
  set(value) {
    if (value !== this._lockMovementX) {
      this._lockMovementX = value;
    }
  },
});

Object.defineProperty(fabric.Object.prototype, 'lockMovementY', {
  get() {
    return this.canvas?.editingSymbol ? true : this._lockMovementY;
  },
  set(value) {
    if (value !== this._lockMovementY) {
      this._lockMovementY = value;
    }
  },
});

Object.defineProperty(fabric.Object.prototype, 'lockScalingX', {
  get() {
    return this.canvas?.editingSymbol ? true : this._lockScalingX;
  },
  set(value) {
    if (value !== this._lockScalingX) {
      this._lockScalingX = value;
    }
  },
});
Object.defineProperty(fabric.Object.prototype, 'lockScalingY', {
  get() {
    return this.canvas?.editingSymbol ? true : this._lockScalingY;
  },
  set(value) {
    if (value !== this._lockScalingY) {
      this._lockScalingY = value;
    }
  },
});

Object.defineProperty(fabric.Object.prototype, 'lockRotation', {
  get() {
    return this.canvas?.editingSymbol ? true : this._lockRotation;
  },
  set(value) {
    if (value !== this._lockRotation) {
      this._lockRotation = value;
    }
  },
});

Object.defineProperty(fabric.Object.prototype, 'strokeWidth', {
  get() {
    return this._strokeWidth;
  },
  set(value) {
    if ((value || value === 0) && value !== this._strokeWidth) {
      this._strokeWidth = value;
      if (this.strokeStyle) {
        // this.strokeStyle = this.strokeStyle;
        this.applyStrokeStyle();
      }
    }
  },
});

function getSvgColorString(prop, value) {
  if (!value) {
    return prop + ': none; ';
  } else if (value.toLive) {
    return prop + ': url(#SVGID_' + value.id + '); ';
  } else {
    var color = new fabric.Color(value);
    var str = prop + ': ' + color.toRgb() + '; ';
    var opacity = color.getAlpha();
    if (opacity !== 1) {
      // change the color in rgb + opacity
      str += prop + '-opacity: ' + opacity.toString() + '; ';
    }
    return str;
  }
}

// Let makes fabric fn moveTo works with activeSelection
// Used by objectorder fn of objectActions.jsx
fabric.Canvas.prototype.moveTo = function(object, index) {
  if (object.type === 'activeSelection') {
    let objsMoved = 0;
    let idx = 0;
    const objs = object._objects;
    for (let i = 0; i < objs.length; i++) {
      const obj = objs[i];
      idx = this._objects.indexOf(obj);
      const direction = idx < index ? 1 : idx > index ? -1 : 0;
      if (direction === 1) {
        // MOVE ACTIVE SELECTION UP
        if (idx <= this._objects.length - 1 - objsMoved) {
          fabric.util.removeFromArray(this._objects, obj);
          this._objects.splice(index, 0, obj);
        }
      } else if (direction === -1) {
        if (index >= 0) {
          fabric.util.removeFromArray(this._objects, obj);
          this._objects.splice(index + objsMoved, 0, obj);
        }
      }
      objsMoved++;
    }
  } else {
    fabric.util.removeFromArray(this._objects, object);
    this._objects.splice(index, 0, object);
  }
  return this.renderOnAddRemove && this.requestRenderAll();
};

fabric.Canvas.prototype._onObjectAdded = function(obj) {
  this.stateful && obj.setupState();
  obj._set('canvas', this);
  !obj.canvasId && obj._set('canvasId', uuid.v4());
  obj.setCoords();
  this.fire('object:added', { target: obj });
  obj.fire('added');
};

fabric.Object.prototype.isControlVisible = function(controlName) {
  if (this.canvas.editingSymbol) {
    return false;
  }
  return this._getControlsVisibility()[controlName];
};

fabric.util.transformPositionToGroupCoords = function(group, position) {
  const groupTransform = group.calcTransformMatrix();
  const iGroupTransform = invertTransform(groupTransform);
  const posRelativeToGroup = transformPoint(position, iGroupTransform);
  return posRelativeToGroup;
};

fabric.util.getByFromAction = function(action) {
  return action === 'scaleX'
    ? 'x'
    : action === 'scaleY'
    ? 'y'
    : action === 'scale'
    ? 'xy'
    : 'equally';
};

fabric.Object.prototype.applyGroupMatrix = function({
  groupMatrix,
  invertGroupMatrix,
  by,
}) {
  const _groupMatrix = groupMatrix || this.group.calcTransformMatrix();
  const _invertGroupMatrix = invertGroupMatrix || invertTransform(_groupMatrix);
  const objectTransform = this.calcTransformMatrix();
  const resetTransform = fabric.util.multiplyTransformMatrices(
    _invertGroupMatrix,
    objectTransform,
  );
  const finalTransform = fabric.util.multiplyTransformMatrices(
    _groupMatrix,
    resetTransform,
  );
  const finalOptions = fabric.util.qrDecompose(finalTransform);
  if (this.group?.type === 'activeSelection') {
    const posRelativeToGroup = fabric.util.transformPositionToGroupCoords(
      this.group,
      { x: finalOptions.translateX, y: finalOptions.translateY },
    );
    finalOptions.translateX = posRelativeToGroup.x;
    finalOptions.translateY = posRelativeToGroup.y;
  }
  const scaleX = finalOptions.scaleX / this.scaleX;
  // TEXT ARE NOT SCALED ON Y AND CONSTRAINT BY ORIGIN WHEN IN GROUP
  const scaleY =
    this.type === 'ludusText'
      ? finalOptions.scaleY
      : finalOptions.scaleY / this.scaleY;
  this.scaleObjectInGroup({
    scaleX,
    scaleY,
    groupOptions: finalOptions,
    by,
  });
};

fabric.Object.prototype.applyObjectMatrix = function({
  transformMatrix,
  width,
  height,
  options,
  by,
}) {
  const _options = options || fabric.util.qrDecompose(transformMatrix);
  if (this instanceof fabric.Group) {
    const scaleX = width / this.width;
    const scaleY = height / this.height;
    this.scaleObjectInGroup({
      by,
      scaleX: scaleX,
      scaleY: scaleY,
    });
  }

  this.width = width;
  this.height = height;
  this.setOptions(_options);
  if (this.group?.type === 'activeSelection') {
    const posRelativeToGroup = fabric.util.transformPositionToGroupCoords(
      this.group,
      { x: _options.translateX, y: _options.translateY },
    );
    _options.translateX = posRelativeToGroup.x;
    _options.translateY = posRelativeToGroup.y;
  }
  this.setPositionByOrigin(
    { x: _options.translateX, y: _options.translateY },
    'center',
    'center',
  );
};

fabric.Canvas.prototype._onObjectRemoved = function(obj) {
  // WHEN DELETING AN OBJECT MAKE SURE OTHER ACTIVE OBJECTS ARE UPDATED ACCORDINGLY
  if (this.otherActiveObjects) {
    this.otherActiveObjects = this.otherActiveObjects.filter(
      o => o.activeObject.canvasId !== obj.canvasId,
    );
  }
  if (obj === this._activeObject) {
    this.fire('before:selection:cleared', { target: obj });
    this._discardActiveObject();
    this.fire('selection:cleared', { target: obj });
    obj.fire('deselected');
  }
  if (obj === this._hoveredTarget) {
    this._hoveredTarget = null;
    this._hoveredTargets = [];
  }
  fabric.StaticCanvas.prototype._onObjectRemoved.call(this, obj);
};

fabric.Canvas.prototype.updateCollaboratorActiveObjects = function(
  collaboratorObjects,
) {
  if (!collaboratorObjects) return null;
  if (
    !collaboratorObjects.length &&
    this.collaboratorActiveObjects &&
    !this.collaboratorActiveObjects.length
  )
    return null;
  const previousActiveObjects = this.collaboratorActiveObjects || [];
  this.collaboratorActiveObjects = collaboratorObjects;
  this.otherActiveObjects = [];

  this.collaboratorActiveObjects.forEach((userData, index) => {
    const previousActiveObject = previousActiveObjects[index];
    const { currentTransform, originalTransform, objects } = userData;
    const options =
      currentTransform &&
      fabric.util.qrDecompose(currentTransform.transformMatrix);
    let invertPreviousGroupMatrix = null;
    let currentGroupMatrix = null;

    // if mutlipe objects, pre-compute first main transform and apply
    // to each object
    if (currentTransform && objects.length > 1) {
      const tm = currentTransform.transformMatrix;
      const ptm =
        previousActiveObject?.currentTransform?.transformMatrix ||
        originalTransform.currentTransform;

      invertPreviousGroupMatrix =
        (ptm && invertTransform(ptm)) ||
        invertTransform(originalTransform.transformMatrix);

      const scaleX = currentTransform.width / originalTransform.width;
      const scaleY = currentTransform.height / originalTransform.height;
      const scaleTransform = [scaleX, 0, 0, scaleY, 0, 0];
      currentGroupMatrix = fabric.util.multiplyTransformMatrices(
        tm,
        scaleTransform,
      );
      currentTransform.transformMatrix = currentGroupMatrix;
    }
    objects.forEach(refObject => {
      const activeObject = this._objects.find(o => o.canvasId === refObject);

      if (activeObject && !activeObject.isEditing) {
        if (
          options &&
          !(
            this._currentTransform?.target &&
            activeObject.canvasId === this._currentTransform.target
          )
        ) {
          const action = currentTransform.action;
          const _by = fabric.util.getByFromAction(action);

          if (!currentGroupMatrix) {
            activeObject.applyObjectMatrix({
              ...currentTransform,
              options,
              by: _by,
            });
          } else if (currentGroupMatrix) {
            activeObject.applyGroupMatrix({
              groupMatrix: currentGroupMatrix,
              invertGroupMatrix: invertPreviousGroupMatrix,
              by: _by,
            });
          }
        }
        this.otherActiveObjects.push({
          activeObject,
          color: userData?.user?.color,
        });
      }
    });
  });
};

fabric.Canvas.prototype.drawControls = function(ctx) {
  var activeObject = this._activeObject;
  if (
    this.showCollaboratorsSelection &&
    this.otherActiveObjects &&
    this.otherActiveObjects.length
  ) {
    this.otherActiveObjects.forEach(o => {
      if (o.color && o.activeObject) {
        if (!o.activeObject.canvas) {
          o.activeObject.canvas = this;
        }
        o.activeObject._renderControls(ctx, {
          borderColor: o.color,
          cornerStrokeColor: o.color,
          hasRotatingPoint: false,
          cornerColor: o.color,
          lineWidth: 2,
          cornerSize: !o.activeObject.group ? 4 : 0,
          forActiveSelection: o.activeObject.group?.type === 'activeSelection',
        });
      }
    });
  }
  if (activeObject) {
    activeObject._renderControls(ctx);
  }
};

fabric.Object.prototype._drawControl = function(
  control,
  ctx,
  methodName,
  left,
  top,
  styleOverride,
) {
  styleOverride = styleOverride || {};
  if (!this.isControlVisible(control)) {
    return;
  }
  var size =
    styleOverride.cornerSize !== undefined
      ? styleOverride.cornerSize
      : this.cornerSize;
  var stroke = !this.transparentCorners && this.cornerStrokeColor;
  switch (styleOverride.cornerStyle || this.cornerStyle) {
    case 'circle':
      ctx.beginPath();
      ctx.arc(left + size / 2, top + size / 2, size / 2, 0, 2 * Math.PI, false);
      ctx[methodName]();
      if (stroke) {
        ctx.stroke();
      }
      break;
    default:
      this.transparentCorners || ctx.clearRect(left, top, size, size);
      ctx[methodName + 'Rect'](left, top, size, size);
      if (stroke) {
        ctx.strokeRect(left, top, size, size);
      }
  }
};

// now all objects are cached. Some functions rely on this
fabric.Object.prototype.shouldCache = fabric.Image.prototype.shouldCache = fabric.Group.prototype.shouldCache = function() {
  return true;
};

fabric.Object.prototype.forEachControl = function(fn) {
  for (var i in this.controls) {
    fn(this.controls[i], i, this);
  }
};

// fix custom line width not being used
fabric.Object.prototype.drawBorders = function(ctx, styleOverride) {
  styleOverride = styleOverride || {};
  var wh = this._calculateCurrentDimensions();
  var strokeWidth = 1 / this.borderScaleFactor;
  var width = wh.x + strokeWidth;
  var height = wh.y + strokeWidth;
  var drawRotatingPoint =
    typeof styleOverride.hasRotatingPoint !== 'undefined'
      ? styleOverride.hasRotatingPoint
      : this.hasRotatingPoint;
  var hasControls =
    typeof styleOverride.hasControls !== 'undefined'
      ? styleOverride.hasControls
      : this.hasControls;
  var rotatingPointOffset =
    typeof styleOverride.rotatingPointOffset !== 'undefined'
      ? styleOverride.rotatingPointOffset
      : this.rotatingPointOffset;

  ctx.save();
  ctx.strokeStyle = styleOverride.borderColor || this.borderColor;
  ctx.lineWidth = styleOverride.lineWidth || ctx.lineWidth;
  this._setLineDash(
    ctx,
    styleOverride.borderDashArray || this.borderDashArray,
    null,
  );

  ctx.strokeRect(-width / 2, -height / 2, width, height);

  if (drawRotatingPoint && this.isControlVisible('mtr') && hasControls) {
    var rotateHeight = -height / 2;

    ctx.beginPath();
    ctx.moveTo(0, rotateHeight);
    ctx.lineTo(0, rotateHeight - rotatingPointOffset);
    ctx.stroke();
  }

  ctx.restore();
  return this;
};

const overridenDispose = fabric.Object.prototype.dispose;

fabric.Object.prototype.dispose = function() {
  if (this._cacheCanvas) {
    // in safari there is a bug that if we dont set size to 0 it wont free memory, even if you set the ref to null
    // this._cacheContext = null;
    // this._cacheCanvas.width = 0;
    // this._cacheCanvas.height = 0;
    // this._cacheCanvas = null;
  }

  overridenDispose && overridenDispose.call(fabric.Object.prototype);
};

// Fabric 3.6.x changed its logic here, we revert back to 3.3.x
// + fix custom line width not being used
fabric.Object.prototype.drawBordersInGroup = function(
  ctx,
  options,
  styleOverride,
) {
  styleOverride = styleOverride || {};
  var p = this._getNonTransformedDimensions();
  var matrix = fabric.util.composeMatrix({
    scaleX: options.scaleX,
    scaleY: options.scaleY,
    skewX: options.skewX,
  });
  var wh = fabric.util.transformPoint(p, matrix);
  var width = wh.x;
  var height = wh.y;

  ctx.save();
  this._setLineDash(
    ctx,
    styleOverride.borderDashArray || this.borderDashArray,
    null,
  );
  ctx.strokeStyle = styleOverride.borderColor || this.borderColor;
  ctx.lineWidth = styleOverride.lineWidth || ctx.lineWidth;
  ctx.strokeRect(-width / 2, -height / 2, width, height);

  ctx.restore();
  return this;
};
const setCursorFromEvent = fabric.Canvas.prototype._setCursorFromEvent;
fabric.Canvas.prototype._setCursorFromEvent = function(e, target) {
  if (this.hoverRulers) {
    this.setCursor(this.hoverRulers === 'x' ? 'ew-resize' : 'ns-resize');
  } else {
    if (this.isSpaceDown) {
      if (e.buttons === 1) {
        this.setCursor('-webkit-grabbing');
      } else {
        this.setCursor('-webkit-grab');
      }
    } else if (this.editingSymbol) {
      this.setCursor(this.defaultCursor);
      return false;
    } else {
      setCursorFromEvent.call(this, e, target);
    }
  }
};

/**
 * Checks point is inside the object.
 * @param {Object} [pointer] x,y object of point coordinates we want to check.
 * @param {fabric.Object} obj Object to test against
 * @param {Object} [globalPointer] x,y object of point coordinates relative to canvas used to search per pixel target.
 * @return {Boolean} true if point is contained within an area of given object
 * @private
 */
fabric.Canvas.prototype._checkTarget = function(pointer, obj, globalPointer) {
  if (
    obj &&
    obj.visible &&
    obj.evented &&
    this.containsPoint(null, obj, pointer)
  ) {
    if ((this.perPixelTargetFind || obj.perPixelTargetFind) && !obj.isEditing) {
      var isTransparent = this.isTargetTransparent(
        obj,
        globalPointer.x,
        globalPointer.y,
      );
      return !isTransparent;
    } else {
      return true;
    }
  }
};

fabric.ElementsParser.prototype.findTag = function(el) {
  const tag = fabric.util.string.capitalize(el.tagName.replace('svg:', ''));
  // Ellipse //Circle
  switch (tag) {
    case 'Circle':
    case 'Ellipse':
      return fabric.LudusCircle;
    case 'Rect':
      return fabric.LudusRect;
    case 'Line':
      return fabric.LudusLine;
    case 'Path':
      return fabric.LudusPath;
    default:
      return fabric[tag];
  }
};

fabric.Line.prototype._setWidthHeight = function(options) {
  options || (options = {});
  // MAKE SURE AT LEAST 1px in height of width otherwise it becomes unselectable  ,
  // it becomes not cachable leading to big blocking error in the
  this.width = Math.max(Math.abs(this.x2 - this.x1), 1);
  this.height = Math.max(Math.abs(this.y2 - this.y1), 1);
  this.left = 'left' in options ? options.left : this._getLeftToOriginX();
  this.top = 'top' in options ? options.top : this._getTopToOriginY();
};

fabric.Line.prototype.calcLinePoints = function() {
  var xMult = this.x1 <= this.x2 ? -1 : 1;
  var yMult = this.y1 <= this.y2 ? -1 : 1;

  var x1 = this.width === 1 ? 0 : xMult * this.width * 0.5;
  var y1 = this.height === 1 ? 0 : yMult * this.height * 0.5;
  var x2 = this.width === 1 ? 0 : xMult * this.width * -0.5;
  var y2 = this.height === 1 ? 0 : yMult * this.height * -0.5;

  return {
    x1: x1,
    x2: x2,
    y1: y1,
    y2: y2,
  };
};

fabric.Object.prototype.getSvgStyles = function(skipShadow) {
  var fillRule = this.fillRule ? this.fillRule : 'nonzero';
  var strokeWidth = this.strokeWidth ? this.strokeWidth : '0';
  var strokeDashArray = this.strokeDashArray
    ? this.strokeDashArray.join(' ')
    : 'none';
  var strokeDashOffset = this.strokeDashOffset ? this.strokeDashOffset : '0';
  var strokeLineCap = this.strokeLineCap ? this.strokeLineCap : 'round';
  var strokeLineJoin = this.strokeLineJoin ? this.strokeLineJoin : 'miter';
  var strokeMiterLimit = this.strokeMiterLimit ? this.strokeMiterLimit : '4';
  var opacity = typeof this.opacity !== 'undefined' ? this.opacity : '1';
  var visibility = this.visible ? '' : ' visibility: hidden;';
  var filter = skipShadow ? '' : this.getSvgFilter();
  var fill = getSvgColorString('fill', this.fill);
  var stroke = getSvgColorString('stroke', this.stroke);

  // START CUSTOM CODE
  if (this.strokeWidth == null && this.stroke) {
    strokeWidth = '1';
  }
  // END CUSTOM CODE

  return [
    stroke,
    'stroke-width: ',
    strokeWidth,
    '; ',
    'stroke-dasharray: ',
    strokeDashArray,
    '; ',
    'stroke-linecap: ',
    strokeLineCap,
    '; ',
    'stroke-dashoffset: ',
    strokeDashOffset,
    '; ',
    'stroke-linejoin: ',
    strokeLineJoin,
    '; ',
    'stroke-miterlimit: ',
    strokeMiterLimit,
    '; ',
    fill,
    'fill-rule: ',
    fillRule,
    '; ',
    'opacity: ',
    opacity,
    ';',
    filter,
    visibility,
  ].join('');
};

Object.defineProperty(fabric.Text.prototype, 'width', {
  get() {
    return this._width;
  },
  set(value) {
    const minWidth = this.getMinWidth ? this.getMinWidth() : 10;
    const lastMinWidth = this.ludus ? this.ludus.lastMinWidth : 0;
    const ludus = { ...this.ludus };
    if (lastMinWidth && (value > lastMinWidth || this.skewX || this.skewY)) {
      this.splitByGrapheme = false;
      delete ludus.lastMinWidth;
    } else if (value < minWidth && this.ludus && !this.ludus.lastMinWidth) {
      ludus.lastMinWidth = minWidth;
      this.splitByGrapheme = true;
    }
    this.ludus = ludus;
    this._width = value;
  },
});

fabric.Canvas.prototype.getPointer = function(e, ignoreZoom) {
  if (this._absolutePointer && !ignoreZoom) {
    return this._absolutePointer;
  }
  if (this._pointer && ignoreZoom) {
    return this._pointer;
  }

  var pointer = fabric.util.getPointer(e);
  var upperCanvasEl = this.upperCanvasEl;
  var bounds = upperCanvasEl.getBoundingClientRect();
  var boundsWidth = bounds.width || 0;
  var boundsHeight = bounds.height || 0;
  var cssScale;

  if (!boundsWidth || !boundsHeight) {
    if ('top' in bounds && 'bottom' in bounds) {
      boundsHeight = Math.abs(bounds.top - bounds.bottom);
    }
    if ('right' in bounds && 'left' in bounds) {
      boundsWidth = Math.abs(bounds.right - bounds.left);
    }
  }

  this.calcOffset();
  pointer.x = pointer.x - this._offset.left;
  pointer.y = pointer.y - this._offset.top;
  if (!ignoreZoom) {
    pointer = this.restorePointerVpt(pointer);
  }

  var retinaScaling = this.getRetinaScaling();
  if (retinaScaling !== 1) {
    pointer.x /= retinaScaling;
    pointer.y /= retinaScaling;
  }

  if (boundsWidth === 0 || boundsHeight === 0) {
    // If bounds are not available (i.e. not visible), do not apply scale.
    cssScale = { width: 1, height: 1 };
  } else {
    cssScale = {
      width: upperCanvasEl.width / boundsWidth,
      height: upperCanvasEl.height / boundsHeight,
    };
  }

  return {
    x: pointer.x * cssScale.width,
    y: pointer.y * cssScale.height,
    date: Date.now(), // Custom ludus code: needed to prevent miss click or drag
  };
};

// PREVENT MISS DRAG OR SCALE
fabric.Canvas.prototype._onMouseMove = function(e) {
  const prevPointer = this._previousPointer;
  if (prevPointer && prevPointer.date && Date.now() - prevPointer.date < 150) {
    const pointer = this.getPointer(e, true);
    if (
      Math.abs(pointer.x - prevPointer.x) < 4 &&
      Math.abs(pointer.y - prevPointer.y) < 4
    ) {
      return false;
    }
  }

  !this.allowTouchScrolling && e.preventDefault && e.preventDefault();
  this.__onMouseMove(e);
};

fabric.Canvas.prototype.findTarget = function(e, skipGroup) {
  if (this.skipTargetFind) {
    return;
  }
  var ignoreZoom = true;
  var pointer = this.getPointer(e, ignoreZoom);
  var activeObject = this._activeObject;
  var aObjects = this.getActiveObjects();
  var activeTarget;
  var activeTargetSubs;
  var isTouch =
    ['touchstart', 'touchmove', 'touchend'].indexOf(e.type) > -1 ||
    e.pointerType === 'touch';

  // first check current group (if one exists)
  // active group does not check sub targets like normal groups.
  // if active group just exits.
  this.targets = [];

  this.hoverRulers =
    this.showRulers && pointer.x <= this.rulersSize
      ? 'x'
      : this.showRulers && pointer.y <= this.rulersSize
      ? 'y'
      : null;
  if (this.hoverRulers) {
    return false;
  }

  if (
    aObjects.length > 1 &&
    !skipGroup &&
    activeObject === this._searchPossibleTargets([activeObject], pointer)
  ) {
    return activeObject;
  }
  // if we hit the corner of an activeObject, let's return that.
  if (
    aObjects.length === 1 &&
    activeObject._findTargetCorner(pointer, isTouch)
  ) {
    return activeObject;
  }
  if (
    aObjects.length === 1 &&
    activeObject === this._searchPossibleTargets([activeObject], pointer)
  ) {
    if (!this.preserveObjectStacking) {
      return activeObject;
    } else {
      activeTarget = activeObject;
      activeTargetSubs = this.targets;
      this.targets = [];
    }
  }
  // search guidelines in priority (regardeless of their zindex)
  if (this.guidelines && this.guidelines.length > 1) {
    const tempTarget = this._searchPossibleTargets(this.guidelines, pointer);
    if (tempTarget && tempTarget.type === 'ludusGuideline') {
      return tempTarget;
    }
  }
  var target = this._searchPossibleTargets(this._objects, pointer);
  if (
    e[this.altSelectionKey] &&
    target &&
    activeTarget &&
    target !== activeTarget
  ) {
    target = activeTarget;
    this.targets = activeTargetSubs;
  }
  return target;
};

fabric.Canvas.prototype._isUniscalePossible = function(e, target) {
  // inverse use of shift (= lock uniscale when pressed)
  return (
    (!e[this.uniScaleKey] || this.uniScaleTransform) &&
    !target.get('lockUniScaling') &&
    target.type !== 'textbox' &&
    target.type !== 'ludusText'
  );
};

// fix bug in fabric to don't listen right click when double/triple clicking
fabric.IText.prototype.onMouseDown = function(options) {
  // CUSTOM FABRIC : add check on e.button
  if (!this.canvas || options.e.button === 2) {
    return;
  }
  this.__newClickTime = +new Date();
  const newPointer = options.pointer;
  if (this.isTripleClick(newPointer)) {
    this.fire('tripleclick', options);
    this._stopEvent(options.e);
  }
  this.__lastLastClickTime = this.__lastClickTime;
  this.__lastClickTime = this.__newClickTime;
  this.__lastPointer = newPointer;
  this.__lastIsEditing = this.isEditing;
  this.__lastSelected = this.selected;
};

fabric.Canvas.prototype._onMouseOut = function(e) {
  var target = this._hoveredTarget;
  this.fire('mouse:out', { target: target, e: e });
  this._hoveredTarget = null;
  target && target.fire('mouseout', { e: e });
};

CanvasRenderingContext2D.prototype.roundedRectangle = function(
  left,
  top,
  width,
  height,
  radiusX,
  radiusY,
) {
  if (!radiusX && !radiusY) {
    this.rect(left, top, width, height);
    return;
  }
  var right = left + width;
  var bottom = top + height;
  var K = (4 * (Math.SQRT2 - 1)) / 3; // constant for circles using Bezier curve.

  this.beginPath();
  // top left
  this.moveTo(left + radiusX, top);
  // top right
  this.lineTo(right - radiusX, top);
  // right top
  this.bezierCurveTo(
    right + radiusX * (K - 1),
    top,
    right,
    top + radiusY * (1 - K),
    right,
    top + radiusY,
  );
  // right bottom
  this.lineTo(right, bottom - radiusY);
  // bottom right
  this.bezierCurveTo(
    right,
    bottom + radiusY * (K - 1),
    right + radiusX * (K - 1),
    bottom,
    right - radiusX,
    bottom,
  );
  // bottom left
  this.lineTo(left + radiusX, bottom);
  // left bottom
  this.bezierCurveTo(
    left + radiusX * (1 - K),
    bottom,
    left,
    bottom + radiusY * (K - 1),
    left,
    bottom - radiusY,
  );
  // left top
  this.lineTo(left, top + radiusY);
  // top left again
  this.bezierCurveTo(
    left,
    top + radiusY * (1 - K),
    left + radiusX * (1 - K),
    top,
    left + radiusX,
    top,
  );
  this.closePath();
};

/* eslint-disable no-undef */
if (typeof OffscreenCanvas !== 'undefined') {
  OffscreenCanvasRenderingContext2D.prototype.roundedRectangle =
    CanvasRenderingContext2D.prototype.roundedRectangle;
}
/* eslint-enable */

fabric.Canvas.prototype._setObjectScale = function(
  localMouse,
  transform,
  lockScalingX,
  lockScalingY,
  by,
  lockScalingFlip,
  _dim,
) {
  const target = transform.target;
  let forbidScalingX = false;
  let forbidScalingY = false;
  let scaled = false;

  let scaleX = (localMouse.x * target.scaleX) / _dim.x;
  let scaleY = (localMouse.y * target.scaleY) / _dim.y;

  const changeX = target.scaleX !== scaleX;
  const changeY = target.scaleY !== scaleY;

  // custom code start ------------------------------------------------------------------
  if (by === 'absoluteX') {
    // absoluteX or Y means you can scale from only 1 side, the other side scale proportionnaly
    scaleY = (localMouse.x / _dim.x) * target.scaleY;
    by = by === 'absoluteX' ? null : 'equally';
  } else if (by === 'absoluteY') {
    scaleX = (localMouse.y / _dim.y) * target.scaleX;
    by = by === 'absoluteY' ? null : 'equally';
  }
  // custom code end --------------------------------------------------------------------

  if (lockScalingFlip && scaleX <= 0 && scaleX < target.scaleX) {
    forbidScalingX = true;
    localMouse.x = 0;
  }

  if (lockScalingFlip && scaleY <= 0 && scaleY < target.scaleY) {
    forbidScalingY = true;
    localMouse.y = 0;
  }
  // add forbidScaling check here to avoid bug when scaling objects very small (it destroyed them)
  if (
    by === 'equally' &&
    !lockScalingX &&
    !lockScalingY &&
    !forbidScalingY &&
    !forbidScalingX
  ) {
    scaled = this._scaleObjectEqually(localMouse, target, transform, _dim);
  } else if (!by) {
    forbidScalingX ||
      lockScalingX ||
      (target.set('scaleX', scaleX) && (scaled = scaled || changeX));
    forbidScalingY ||
      lockScalingY ||
      (target.set('scaleY', scaleY) && (scaled = scaled || changeY));
  } else if (by === 'x' && !target.get('lockUniScaling')) {
    forbidScalingX ||
      lockScalingX ||
      (target.set('scaleX', scaleX) && (scaled = scaled || changeX));
  } else if (by === 'y' && !target.get('lockUniScaling')) {
    forbidScalingY ||
      lockScalingY ||
      (target.set('scaleY', scaleY) && (scaled = scaled || changeY));
  }
  transform.newScaleX = scaleX;
  transform.newScaleY = scaleY;
  forbidScalingX || forbidScalingY || this._flipObject(transform, by);
  return scaled;
};
const setObjectScaleOverridden = fabric.Canvas.prototype._setObjectScale;

fabric.Object.prototype.shouldDeformObject = function() {
  const o = this;
  if (o.type === 'ludusLine') {
    return false;
  }

  if (
    o.type === 'ludusBrushPath' ||
    o.type === 'ludusPathGroup' ||
    o.type === 'path'
  ) {
    return true;
  }

  if (o.group && o.group.type === 'ludusPathGroup') {
    return true;
  }

  return !(
    Math.round(o.skewX * 100) === 0 &&
    Math.round(o.skewY * 100) === 0 &&
    Math.abs(o.angle % 180) === 0 &&
    (o.type === 'group' ||
      (Math.round(o.skewY * 100) === 0 &&
        Math.round(o.skewX * 100) === 0 &&
        Math.abs(o.angle % 180) === 0 &&
        !o.lockUniScaling &&
        (o.type.includes('ludus') || o.type === 'group') &&
        o.type !== 'ludusPath' &&
        o.type !== 'ludusPathGroup'))
  );
};

fabric.Object.prototype.drawCacheOnCanvas = function(ctx) {
  if (this._cacheCanvas?.width && this._cacheCanvas.height) {
    ctx.scale(1 / this.zoomX, 1 / this.zoomY);
    ctx.drawImage(
      this._cacheCanvas,
      -this.cacheTranslationX,
      -this.cacheTranslationY,
    );
  }
};

fabric.StaticCanvas.prototype.renderCanvas = function(ctx, objects) {
  var v = this.viewportTransform;
  var path = this.clipPath;
  this.cancelRequestedRender();
  this.calcViewportBoundaries();
  this.clearContext(ctx);
  this.fire('before:render', { ctx: ctx });
  if (this.clipTo) {
    fabric.util.clipContext(this, ctx);
  }
  this._renderBackground(ctx);

  ctx.save();
  // apply viewport transform once for all rendering process

  ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
  this.iVPT = invertTransform(v);
  this.VPT = v;
  this._renderObjects(ctx, objects);
  ctx.restore();
  if (!this.controlsAboveOverlay && this.interactive) {
    this.drawControls(ctx);
  }
  if (this.clipTo) {
    ctx.restore();
  }
  if (path) {
    path.canvas = this;
    path.shouldCache = () => true;
    // needed to setup a couple of variables
    path.shouldCache();
    path._transformDone = true;
    path.renderCache({ forClipping: true });
    this.drawClipPathOnCanvas(ctx);
  }
  this._renderOverlay(ctx);
  if (this.controlsAboveOverlay && this.interactive) {
    this.drawControls(ctx);
  }
  this.fire('after:render', { ctx: ctx });
  // TO DO IMPLEMENT ZOOMING VS PANNING

  if (!this.throttledTransformningVPT) {
    this.throttledTransformningVPT = throttle(
      () => {
        this.transformingViewport = false;
        this.zoomingViewport = false;
        this.panningViewport = false;
        this.requestRenderAll();
      },
      100,
      { leading: false, trailing: true },
    );
  }
  if (this.transformingViewport) {
    this.throttledTransformningVPT();
  }
};

const getIntersectingRectangle = (r1, r2) => {
  [r1, r2] = [r1, r2].map(r => {
    return {
      x: [r.left, r.left + r.width].sort((a, b) => a - b),
      y: [r.top, r.top + r.height].sort((a, b) => a - b),
    };
  });

  const noIntersect =
    r2.x[0] > r1.x[1] ||
    r2.x[1] < r1.x[0] ||
    r2.y[0] > r1.y[1] ||
    r2.y[1] < r1.y[0];

  return noIntersect
    ? false
    : {
        x1: Math.max(r1.x[0], r2.x[0]), // _[0] is the lesser,
        y1: Math.max(r1.y[0], r2.y[0]), // _[1] is the greater
        x2: Math.min(r1.x[1], r2.x[1]),
        y2: Math.min(r1.y[1], r2.y[1]),
      };
};

fabric.Canvas.prototype.setViewportTransform = function(vpt) {
  if (
    this.renderOnAddRemove &&
    this._activeObject &&
    this._activeObject.isEditing
  ) {
    this._activeObject.clearContextTop();
  }
  // store when viewport is transforming
  this.transformingViewport = true;
  if (
    vpt[0] !== this.viewportTransform[0] ||
    vpt[3] !== this.viewportTransform[3]
  ) {
    this.zoomingViewport = true;
  } else {
    this.zoomingViewport = false;
  }
  if (
    vpt[4] !== this.viewportTransform[4] ||
    vpt[5] !== this.viewportTransform[5]
  ) {
    this.panningViewport = true;
  } else {
    this.panningViewport = false;
  }

  fabric.StaticCanvas.prototype.setViewportTransform.call(this, vpt);
};

// With the Ludus cache approach we must adapt this method
// The Cache is already transformed so we only need the invert of the obj translateMatrix
fabric.Canvas.prototype.isTargetTransparent = function(target, x, y) {
  // in case the target is the activeObject, we cannot execute this optimization
  // because we need to draw controls too.
  if (
    target.shouldCache() &&
    target._cacheCanvas &&
    target !== this._activeObject
  ) {
    var m = target._calcTranslateMatrix();
    var invertedM = fabric.util.invertTransform(m);
    var finalM = multiplyMatrices(
      invertedM,
      invertTransform(this.viewportTransform),
    );
    var normalizedPointer = transformPoint({ x, y }, finalM);

    var targetRelativeX = Math.max(
      target.cacheTranslationX + normalizedPointer.x * target.zoomX,
      0,
    );
    var targetRelativeY = Math.max(
      target.cacheTranslationY + normalizedPointer.y * target.zoomY,
      0,
    );

    var isCacheTransparent = fabric.util.isTransparent(
      target._cacheContext,
      Math.round(targetRelativeX),
      Math.round(targetRelativeY),
      this.targetFindTolerance,
    );

    return isCacheTransparent;
  }

  var ctx = this.contextCache;
  var originalColor = target.selectionBackgroundColor;
  var v = this.viewportTransform;

  target.selectionBackgroundColor = '';

  this.clearContext(ctx);

  ctx.save();
  ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
  target.render(ctx);
  ctx.restore();

  target === this._activeObject &&
    target._renderControls(
      ctx,
      {
        hasBorders: false,
        transparentCorners: false,
      },
      {
        hasBorders: false,
      },
    );

  target.selectionBackgroundColor = originalColor;

  var isTransparent = fabric.util.isTransparent(
    ctx,
    x,
    y,
    this.targetFindTolerance,
  );

  return isTransparent;
};

// TODO when zoomed in al ot the bounds widht and height are not enoug
// we must check VPT left and top as well
fabric.Object.prototype.getCacheUID = function(bounds) {
  const props = [
    ...variables.propsToSave,
    'rx',
    'ry',
    'strokeWidth',
    'stroke',
    'rendered',
    'fill',
    'charSpacing',
  ]
    .filter(prop => !['top', 'left', 'ludus'].includes(prop))
    .map(prop => JSON.stringify({ [prop]: this[prop] }));
  return `${this.dirty} ${this.canvas.viewportTransform[0]} ${bounds.width} ${
    bounds.height
  } ${this.width} ${this.height} ${this.angle} ${props.join('_')} ${
    this._image?.rendered
  }`;
};

const shouldUpdateCache = (object, bounds, canvasBounds, cacheUID, padding) => {
  if (object.skipCache || object.type === 'path') {
    return false;
  }
  if (object.dirty) {
    return true;
  }
  return (
    ((object.canvas.transformingViewport ||
      object.canvas._currentTransform?.target === object) &&
      (bounds?.width >= canvasBounds?.width ||
        bounds?.height >= canvasBounds?.height)) ||
    (object._cacheCanvas &&
      bounds?.width >= 1 &&
      bounds?.height >= 1 &&
      !(object.prevCacheUID && object.prevCacheUID === cacheUID))
  );
};

fabric.Object.prototype.render = function(ctx) {
  // do not render if width/height are zeros or object is not visible
  if (this.isNotVisible()) {
    return;
  }
  if (
    this.canvas &&
    this.canvas.skipOffscreen &&
    !this.group &&
    !this.isOnScreen()
  ) {
    return;
  }
  ctx.save();

  if (
    this.type !== 'imageAsync' &&
    this.type !== 'image' &&
    !this.skipCache &&
    this.type !== 'path' &&
    !(this.group && this.type === 'ludusPath') &&
    // symbols can have texts growing outside group boundaries and cut if cached
    // TODO: grow cache size accordingly?
    // LudusSymbol transparency is managed by the render method of the class
    // not by the draw cache as for other group based object
    !(this.type === 'ludusSymbol')
  ) {
    const objectBounds = this.getBoundingRectForCaching(ctx);
    // const VPT = this.canvas.VPT;
    const iVPT = this.canvas.iVPT;
    var canvasBounds = {
      left: 0,
      top: 0,
      width: this.canvas.width,
      height: this.canvas.height,
    };
    const bounds = getIntersectingRectangle(objectBounds, canvasBounds);

    if (bounds) {
      bounds.width = Math.ceil(bounds.x2 - bounds.x1);
      bounds.height = Math.ceil(bounds.y2 - bounds.y1);
      const VPT = this.canvas.VPT;
      if (this.shadow && this.shadow.blur > 0 && VPT[0] < 20) {
        const padding = Math.min(this.cachePadding * VPT[0], 2000);
        bounds.x1 -= padding;
        bounds.width = Math.ceil(bounds.width + padding * 2); // (this.shadow.blur * 2 + this.shadow.offsetX) *
        bounds.y1 -= padding;
        bounds.height = Math.ceil(bounds.height + padding * 2); // (this.shadow.blur * 2 + this.shadow.offsetY) * VPT[3];
      }
      if (fabric.debug) {
        ctx.save();

        if (this.group && this.group.type === 'group') {
          const igrp = invertTransform(this.group.calcTransformMatrix());
          ctx.transform(...igrp);
          //
        }
        ctx.transform(...iVPT);
        ctx.strokeStyle = 'magenta';
        ctx.lineWidth = 2;

        ctx.strokeRect(bounds.x1, bounds.y1, bounds.width, bounds.height);

        ctx.restore();
      }

      if (!this._cacheContext) {
        this._createCacheCanvas();
      }
      const cacheUID = this.getCacheUID(bounds);
      if (bounds.width && bounds.height) {
        this.drawCache(
          bounds,
          objectBounds,
          canvasBounds,
          cacheUID,
          ctx,
          this.canvas.transformingViewport,
        );
      }
    }
  } else {
    this._setupCompositeOperation(ctx);
    this.transform(ctx);
    this.drawObject(ctx);
    this.dirty = false;
  }
  ctx.restore();
};

fabric.Object.prototype.drawCache = function(
  bounds,
  objectBounds, // {x1,y1, x2,y2}
  canvasBounds, // {left, top, width, height}
  cacheUID,
  ctx,
  direct,
) {
  const VPT = this.canvas.VPT;
  const iVPT = this.canvas.iVPT;

  if (
    !(direct && this.cached) &&
    shouldUpdateCache(this, bounds, canvasBounds, cacheUID)
  ) {
    const resolution = bounds.width * 2 <= 4000 ? 2 : 1; // Usefull to set to 2 for crispier text rendering?
    this._cacheCanvas.width = bounds.width * resolution;
    this._cacheCanvas.height = bounds.height * resolution;
    this._cacheCanvas.bounds = bounds;
    this.prevCacheUID = cacheUID;
    const m = this.calcTransformMatrix();
    let offsetX = objectBounds.left < 0 ? objectBounds.left : 0;
    let offsetY = objectBounds.top < 0 ? objectBounds.top : 0;
    // TODO MAX THE SHADOW PADDING
    if (this.shadow && this.shadow.blur > 0 && VPT[0] < 20) {
      const padding = Math.min(this.cachePadding * VPT[0], 2000);
      offsetX += padding;
      offsetY += padding;
    }
    this.cacheTranslationX = objectBounds.width / 2 + offsetX; // offsetX;
    this.cacheTranslationY = objectBounds.height / 2 + offsetY; // offsetY;
    this.zoomX = VPT[0];
    this.zoomY = VPT[3];

    this._cacheContext.scale(resolution, resolution);
    this._cacheContext.translate(offsetX, offsetY);
    this._cacheContext.transform(
      m[0],
      m[1],
      m[2],
      m[3],
      objectBounds.width / 2,
      objectBounds.height / 2,
    );
    const fullyVisible =
      bounds.x1 === objectBounds.x1 &&
      bounds.y2 === objectBounds.y2 &&
      bounds.x2 === objectBounds.x2 &&
      bounds.y1 === objectBounds.y1;

    if (fullyVisible) {
      this.cached = true;
    } else {
      this.cached = false;
    }
    this._cacheContext.scale(VPT[0], VPT[3]);

    this.drawObject(this._cacheContext);
  }

  if (this.group && this.group.type !== 'activeSelection') {
    const igrp = invertTransform(this.group.calcTransformMatrix());

    ctx.transform(...igrp);
  }
  this._setOpacity(ctx);
  // if (VPT[0] < 20) {
  this._setShadow(ctx, this);
  // }
  this._setupCompositeOperation(ctx);

  this.prevDirect = direct;
  if (this._cacheCanvas.width && this._cacheCanvas.height) {
    ctx.transform(...iVPT);
    if (this instanceof fabric.Text) {
      ctx.imageSmoothingEnabled = false;
    } else {
      // ctx.imageSmoothingEnabled = true;
    }

    if (direct && this.cached) {
      ctx.drawImage(
        this._cacheCanvas,
        objectBounds.left,
        objectBounds.top,
        Math.ceil(objectBounds.width),
        Math.ceil(objectBounds.height),
      );
    } else {
      ctx.drawImage(
        this._cacheCanvas,
        bounds.x1,
        bounds.y1,
        bounds.width,
        bounds.height,
      );
    }
  }
};

function arrayFromCoords(coords) {
  return [
    new fabric.Point(coords.tl.x, coords.tl.y),
    new fabric.Point(coords.tr.x, coords.tr.y),
    new fabric.Point(coords.br.x, coords.br.y),
    new fabric.Point(coords.bl.x, coords.bl.y),
  ];
}

const makeBoundingBoxFromPoints = (points, transform, target, offsetBounds) => {
  const style_expansion = 0;
  // if (
  //   target.strokeMiterLimit &&
  //   target.strokeLineJoin === 'miter' &&
  //   target.stroke &&
  //   target.stroke !== 'transparent' &&
  //   target.strokeWidth > 0
  // ) {
  //   style_expansion = M_SQRT1_2 * target.strokeMiterLimit;
  //   style_expansion *= target.strokeWidth * target.canvas.getZoom() || 1;
  // }
  if (transform) {
    for (var i = 0; i < points.length; i++) {
      points[i] = fabric.util.transformPoint(points[i], transform);
    }
  }

  const padding =
    (target.cachePadding || 0) * target.canvas.viewportTransform[0];

  var xPoints = [points[0].x, points[1].x, points[2].x, points[3].x];
  var minX = fabric.util.array.min(xPoints) - style_expansion - padding;
  var maxX = fabric.util.array.max(xPoints) + style_expansion + padding;
  var width = maxX - minX;
  var yPoints = [points[0].y, points[1].y, points[2].y, points[3].y];
  var minY = fabric.util.array.min(yPoints) - style_expansion - padding;
  var maxY = fabric.util.array.max(yPoints) + style_expansion + padding;
  var height = maxY - minY;

  return {
    left: minX,
    top: minY,
    width: width,
    height: height,
    x1: minX,
    x2: maxX,
    y1: minY,
    y2: maxY,
  };
};

fabric.Object.prototype.getBoundingRectForCaching = function(ctx, transform) {
  if (!this.oCoords) {
    this.setCoords();
  }
  var coords = arrayFromCoords(this.calcCoords(false, true, ctx));
  return makeBoundingBoxFromPoints(coords, transform, this);
};

fabric.Object.prototype.calcCoords = function(absolute, applyGroup, ctx) {
  var rotateMatrix = this._calcRotateMatrix();
  var translateMatrix = this._calcTranslateMatrix();
  var startMatrix = multiplyMatrices(translateMatrix, rotateMatrix);

  if (applyGroup && this.group && this.type !== 'imageAsync') {
    startMatrix = multiplyMatrices(
      this.group.calcTransformMatrix(),
      startMatrix,
    );
  }
  var vpt = this.getViewportTransform();
  var finalMatrix = absolute ? startMatrix : multiplyMatrices(vpt, startMatrix);
  var dim = this._getTransformedDimensions();
  var w = dim.x / 2;
  var h = dim.y / 2;
  var tl = transformPoint({ x: -w, y: -h }, finalMatrix);
  var tr = transformPoint({ x: w, y: -h }, finalMatrix);
  var bl = transformPoint({ x: -w, y: h }, finalMatrix);
  var br = transformPoint({ x: w, y: h }, finalMatrix);

  if (!absolute) {
    var padding = this.padding;
    var angle = degreesToRadians(this.angle);
    var cos = fabric.util.cos(angle);
    var sin = fabric.util.sin(angle);
    var cosP = cos * padding;
    var sinP = sin * padding;
    var cosPSinP = cosP + sinP;
    var cosPMinusSinP = cosP - sinP;
    if (padding) {
      tl.x -= cosPMinusSinP;
      tl.y -= cosPSinP;
      tr.x += cosPSinP;
      tr.y -= cosPMinusSinP;
      bl.x -= cosPSinP;
      bl.y += cosPMinusSinP;
      br.x += cosPMinusSinP;
      br.y += cosPSinP;
    }
    var ml = new fabric.Point((tl.x + bl.x) / 2, (tl.y + bl.y) / 2);
    var mt = new fabric.Point((tr.x + tl.x) / 2, (tr.y + tl.y) / 2);
    var mr = new fabric.Point((br.x + tr.x) / 2, (br.y + tr.y) / 2);
    var mb = new fabric.Point((br.x + bl.x) / 2, (br.y + bl.y) / 2);
    var mtr = new fabric.Point(
      mt.x + sin * this.rotatingPointOffset,
      mt.y - cos * this.rotatingPointOffset,
    );
  }

  var coords = { tl, tr, br, bl };

  /// ////////////
  // Check if object has shadow or if a group check shadow of childs?
  this.cachePadding = 0;
  if (this.shadow) {
    this.cachePadding =
      Math.max(Math.abs(this.shadow.offsetX), Math.abs(this.shadow.offsetY)) +
      this.shadow.blur;
  }
  // Some fonts have wrong metrics and can be a little cut (ie: niche light.otf)
  // Adding a small padding help in most cases
  if (this instanceof fabric.Text) {
    this.cachePadding = this.scaleX * this.fontSize * 0.15;
  }

  if (this instanceof fabric.Group) {
    const maxChildPadding = max(this._objects.map(o => o.cachePadding));

    this.cachePadding += maxChildPadding;
  }
  // per child find the largest shadow offset
  // Math.abs(this.shadow.offsetX) + this.shadow.blur / 2
  // add it to the group shadow offset

  // ALT:
  // add a padding for cache on everything object.cachePadding
  // group use biggest cachePadding of childs to compose with their own cachePadding
  // Cache padding will be useful as well for miter angle problematic

  // there is still two problematic to keep in mind: object get invisible but the shadow shoud still be visible
  // => Use the cachePadding to detect visibilty?
  // an object clipped by canvas boundary will get its shadow cut as well, we need a padding for the clip as well
  // but it can dangeroulsy lead to too big objects

  /// -----------

  if (!absolute) {
    coords.ml = ml;
    coords.mt = mt;
    coords.mr = mr;
    coords.mb = mb;
    // rotating point
    coords.mtr = mtr;
  }
  return coords;
};
fabric.Object.prototype.scaleObjectInGroup = function({
  scaleX,
  scaleY,
  _by,
  groupOptions,
}) {
  const o = this;
  const prevFlips = { flipX: o.flipX, flipY: o.flipY }; // there is a bug with how Fabric compute matrix with flip. We restore that after transformation
  o.flipX = false;
  o.flipY = false;
  const ownMatrix = o.calcOwnMatrix();
  const results = fabric.util.multiplyTransformMatrices(
    [scaleX, 0, 0, scaleY, 0, 0],
    ownMatrix,
  );

  // you can call scaleObjectInGroup for objects not actually in group (ie: for remote users)
  // then you must apply the group transform before calling this function and pass the computed options
  const options = groupOptions || fabric.util.qrDecompose(results);
  const prevHeight = o.height;
  if (!o.shouldDeformObject()) {
    o.set('width', (options.scaleX / o.scaleX) * o.width);
    o.applyScaleY && o.applyScaleY(options.scaleY / o.scaleY);
    o.type !== 'ludusText' &&
      o.set('height', (options.scaleY / o.scaleY) * o.height);
    const center = o.getCenterPoint();

    // transformed position is used for object needing specific contraint position.
    // Texts in groups are not scaled vertically. And we will use the originY as contraint pos.
    // In that case we apply the scale to the point based on origin not to the center point.
    // Texts can then stick to top or bottom edge of the group.
    // todo: we can implement custom constraint position for any type of object and
    // use apply the contraint to transformedPosition.
    let transformedPosition =
      o.type === 'ludusText'
        ? { x: options.translateX, y: o.top * scaleY }
        : { x: options.translateX, y: options.translateY };
    if (o.type === 'ludusText' && groupOptions) {
      switch (o.originY) {
        case 'bottom':
          transformedPosition = {
            x: options.translateX,
            y: options.translateY + (prevHeight / 2) * scaleY,
          };
          break;
        case 'top':
          transformedPosition = {
            x: options.translateX,
            y: options.translateY - (prevHeight / 2) * scaleY,
          };
          break;
        default:
          break;
      }
    }

    o.setPositionByOrigin(
      {
        x: _by !== 'y' ? transformedPosition.x : center.x,
        // TODO RESET INITIAL TOP DIFFERENCE
        y: _by !== 'x' ? transformedPosition.y : center.y,
      },
      'center',
      o.type === 'ludusText' && _by !== 'x' ? o.originY || 'center' : 'center',
    );
    o.angle = options.angle;
    o.flipX = prevFlips.flipX;
    o.flipY = prevFlips.flipY;
    if (o.type === 'group' || o.type === 'ludusSymbol') {
      o.forEachObject(child =>
        child.scaleObjectInGroup({
          scaleX: scaleX,
          scaleY: scaleY,
          _by,
        }),
      );
    }
    if (o.updateGradient) {
      o.updateGradient();
    }
  } else {
    o.set({
      flipX: false,
      flipY: false,
    });
    o.set(options);
    o.setPositionByOrigin(
      {
        x: options.translateX,
        y: options.translateY,
      },
      'center',
      'center',
    );
  }
};

fabric.Canvas.prototype._setObjectScale = function(
  localMouse,
  transform,
  lockScalingX,
  lockScalingY,
  by,
  lockScalingFlip,
  _dim,
) {
  const t = transform.target;
  const _by = by;
  const dim = t._getTransformedDimensions();
  let scalingFactorX = _by !== 'y' ? localMouse.x / dim.x : 1;
  let scalingFactorY = _by !== 'x' ? localMouse.y / dim.y : 1;

  if (by === 'equally') {
    if (scalingFactorX > scalingFactorY) {
      scalingFactorY = scalingFactorX;
    } else {
      scalingFactorX = scalingFactorY;
    }
  }
  if (by === 'absoluteX') {
    scalingFactorY = scalingFactorX;
  }
  if (by === 'absoluteY') {
    scalingFactorX = scalingFactorY;
  }

  const computedW = t.width * scalingFactorX;
  const computedH = t.height * scalingFactorY;

  if (scalingFactorX < 0 || scalingFactorY < 0) {
    return false;
  }
  if (t instanceof fabric.ActiveSelection) {
    t.forEachObject(o => {
      if (o.lockUniScaling) {
        t.lockUniScaling = true;
      }
    });
  }

  if (
    t instanceof fabric.Group &&
    !t.lockUniScaling &&
    by !== 'equally' &&
    by !== 'absoluteX' &&
    by !== 'absoluteY'
  ) {
    const allMinWidth = [];
    t.forEachObject(o => {
      o.scaleObjectInGroup({
        scaleX: scalingFactorX,
        scaleY: scalingFactorY,
        _by,
      });

      if (o.dynamicMinWidth && !o.shouldDeformObject()) {
        allMinWidth.push(o.dynamicMinWidth * o.scaleX);
      }
    });
    const minWidth = max(allMinWidth);
    if (computedW > minWidth || allMinWidth.length === 0) {
      t.set('width', computedW);
      t.set('height', computedH);
      return true;
    } else {
      t.set('width', minWidth);
      t.set('height', computedH);
      return false;
    }
  } else if (_by === 'x' && t instanceof fabric.Textbox) {
    t.set('width', computedW);
    return true;
  }

  // custom code end --------------------------------------------------------------------
  return setObjectScaleOverridden.call(
    fabric.Canvas.prototype,
    localMouse,
    transform,
    lockScalingX,
    lockScalingY,
    _by,
    lockScalingFlip,
    _dim,
  );
};

/*
 * Fabric monkey patch
 * Remove object scale influence on shadows
 *
 */
fabric.Object.prototype._setShadow = function(ctx, target, transform) {
  if (!this.shadow) {
    return;
  }

  var multX = (this.canvas && this.canvas.viewportTransform[0]) || 1;
  var multY = (this.canvas && this.canvas.viewportTransform[3]) || 1;

  if (this.canvas && this.canvas._isRetinaScaling()) {
    multX *= fabric.devicePixelRatio;
    multY *= fabric.devicePixelRatio;
  }

  // if (this.group && this.group.type !== 'activeSelection') {
  //   multX /= fabric.devicePixelRatio;
  //   multY /= fabric.devicePixelRatio;
  // }

  // custom code start ------------------------------------------------------------------
  ctx.shadowBlur =
    (this.shadow.blur * fabric.browserShadowBlurConstant * (multX + multY)) / 4;
  ctx.shadowOffsetX = this.shadow.offsetX * multX;
  ctx.shadowOffsetY = this.shadow.offsetY * multY;
  // custom code end --------------------------------------------------------------------
  ctx.shadowColor = this.shadow.color;
};

// fabric.Object.prototype._createCacheCanvas = function() {
//   if (typeof OffscreenCanvas !== 'undefined') {
//     this._cacheCanvas = new OffscreenCanvas(100, 100);
//   } else {
//     this._cacheCanvas = fabric.util.createCanvasElement();
//   }
//   this._cacheProperties = {};
//   this._cacheContext = this._cacheCanvas.getContext('2d');
//   return this._cacheCanvas;
// };

/*
 * Fabric monkey patch
 * Dispatch a `viewport:zoomed` event on canvas
 *
 * TODO: fork Fabric and submit a PR
 */

fabric.StaticCanvas.prototype.zoomToPoint = function(point, value) {
  // TODO: just change the scale, preserve other transformations
  const before = point;
  const vpt = this.viewportTransform.slice(0);
  point = transformPoint(point, invertTransform(this.viewportTransform));
  vpt[0] = value;
  vpt[3] = value;
  const after = transformPoint(point, vpt);
  vpt[4] += before.x - after.x;
  vpt[5] += before.y - after.y;
  const result = this.setViewportTransform(vpt);
  // custom code start ------------------------------------------------------------------
  this.fire('viewport:zoomed', { target: this, zoom: value });
  // custom code end --------------------------------------------------------------------
  return result;
};
/* eslint-enable */

/*
 * Fabric monkey patch
 * Dispatch a `viewport:panned` event on canvas
 *
 * TODO: fork Fabric and submit a PR
 */

fabric.StaticCanvas.prototype.setViewportTransform = function(vpt) {
  var activeObject = this._activeObject;
  var object;
  var ignoreVpt = false;
  var skipAbsolute = true;
  var i;
  var len;
  // custom code start ------------------------------------------------------------------
  var hasPanned = false;
  if (
    this.viewportTransform[4] !== vpt[4] ||
    this.viewportTransform[5] !== vpt[5]
  ) {
    // Viewport center  has changed
    hasPanned = true;
  }
  // custom code end --------------------------------------------------------------------
  this.viewportTransform = vpt;
  for (i = 0, len = this._objects.length; i < len; i++) {
    object = this._objects[i];
    object.group || object.setCoords(ignoreVpt, skipAbsolute);
  }
  if (activeObject && activeObject.type === 'activeSelection') {
    activeObject.setCoords(ignoreVpt, skipAbsolute);
  }
  this.calcViewportBoundaries();
  this.renderOnAddRemove && this.requestRenderAll();
  // custom code start ------------------------------------------------------------------
  if (hasPanned) {
    this.fire('viewport:panned', { target: this, center: this.getVpCenter() });
  }
  // custom code end --------------------------------------------------------------------
  return this;
};

fabric.StaticCanvas.prototype.asyncToDataURL = function(callback, params) {
  const _params = params || {
    format: 'jpg',
    multiplier: 1,
    quality: 0.85,
  };
  const hasImages = fabric.util.objectContainsType(this, [
    'imageAsync',
    'ludusChart',
    'ludusRich',
    'ludusIframe',
  ]);

  this.off('object:allImagesRendered');
  if (hasImages) {
    this.on('object:allImagesRendered', () => {
      const data = this.toDataURL(_params);

      callback && callback(data);
    });
  } else {
    const data = this.toDataURL(_params);
    callback && callback(data);
  }
};

/*
 * Fabric monkey patch
 * Replaces `target.type === 'group'` with a function checking whether the object
 * is a group or inherits from group
 *
 */
fabric.util.inheritsFromGroup = function(target) {
  let proto = target;
  let type;
  do {
    proto = proto.__proto__; // eslint-disable-line no-proto
    type = proto.type;
    if (type === 'group') {
      return true;
    }
  } while (type !== 'object' && type !== undefined);
  return false;
};

fabric.Canvas.prototype.renderAll = function() {
  if (this.contextTopDirty && !this._groupSelector && !this.isDrawingMode) {
    this.clearContext(this.contextTop);
    this.contextTopDirty = false;
    // do not render top layer if currently selecting or in drawing state
    if (this.hasLostContext) {
      this.renderTopLayer(this.contextTop);
    }
  }

  var canvasToDrawOn = this.contextContainer;
  this.renderCanvas(canvasToDrawOn, this._chooseObjectsToRender());
  return this;
};

fabric.StaticCanvas.prototype._toObjects = function(
  methodName,
  propertiesToInclude,
) {
  return this._objects
    .filter(function(object) {
      return !object.excludeFromExport;
    })
    .map(function(instance, index) {
      const object = this._toObject(instance, methodName, propertiesToInclude);
      return {
        ...object,
        ludus: {
          ...object.ludus,
          order: index,
        },
      };
    }, this);
};

fabric.Object.prototype.toObjectUnGrouped = function(propertiesToInclue) {
  var originalProperties = this.canvas._realizeGroupTransformOnObject(this);
  const options = this.toObject(propertiesToInclue);
  this.canvas._unwindGroupTransformOnObject(this, originalProperties);
  return options;
};

fabric.Object.prototype.toObject = function(propertiesToInclude) {
  var toFixed = fabric.util.toFixed;
  var NUM_FRACTION_DIGITS = fabric.Object.NUM_FRACTION_DIGITS;

  var object = {
    type: this.type,
    version: fabric.version,
    originX: this.originX,
    originY: this.originY,
    left: toFixed(this.left, NUM_FRACTION_DIGITS),
    top: toFixed(this.top, NUM_FRACTION_DIGITS),
    width: toFixed(this.width, NUM_FRACTION_DIGITS),
    height: toFixed(this.height, NUM_FRACTION_DIGITS),
    fill:
      this.fill && this.fill.toObject
        ? this.fill.toObject()
        : this.fill === null
        ? false
        : this.fill,
    stroke:
      this.stroke && this.stroke.toObject
        ? this.stroke.toObject()
        : this.stroke === null
        ? false
        : this.stroke,
    strokeWidth: this.strokeWidth
      ? toFixed(this.strokeWidth, NUM_FRACTION_DIGITS)
      : 0,
    styles: 0,
    strokeDashArray: this.strokeDashArray ? this.strokeDashArray.concat() : 0,
    lockUniScaling: this.lockUniScaling,
    strokeLineCap: this.strokeLineCap,
    strokeDashOffset: this.strokeDashOffset,
    strokeLineJoin: this.strokeLineJoin,
    strokeMiterLimit: toFixed(this.strokeMiterLimit, NUM_FRACTION_DIGITS),
    scaleX: toFixed(this.scaleX, NUM_FRACTION_DIGITS),
    scaleY: toFixed(this.scaleY, NUM_FRACTION_DIGITS),
    angle: toFixed(this.angle, NUM_FRACTION_DIGITS),
    flipX: this.flipX,
    flipY: this.flipY,
    opacity: toFixed(this.opacity, NUM_FRACTION_DIGITS),
    shadow:
      this.shadow && this.shadow.toObject
        ? this.shadow.toObject()
        : this.shadow || 0,
    visible: this.visible,
    clipTo: this.clipTo ? String(this.clipTo) : 0,
    backgroundColor: this.backgroundColor,
    fillRule: this.fillRule,
    paintFirst: this.paintFirst,
    globalCompositeOperation: this.globalCompositeOperation,
    transformMatrix: this.transformMatrix ? clone(this.transformMatrix) : 0,
    skewX: toFixed(this.skewX, NUM_FRACTION_DIGITS),
    skewY: toFixed(this.skewY, NUM_FRACTION_DIGITS),
  };

  if (this.clipPath && this.clipPath.toObject) {
    object.clipPath = this.clipPath.toObject(propertiesToInclude);
    object.clipPath.inverted = this.clipPath.inverted;
    object.clipPath.absolutePositioned = this.clipPath.absolutePositioned;
  }

  fabric.util.populateWithProperties(
    this,
    object,
    without(propertiesToInclude, 'ludus'),
  );
  set(object, 'ludus', {
    ...this.ludus,
    transformMatrix: this.calcTransformMatrix(true),
  });

  return object;
};

fabric.IText.prototype._wrapSVGTextAndBg = function(textAndBg) {
  var noShadow = true;
  var textDecoration = this.getSvgTextDecoration(this);
  return [
    textAndBg.textBgRects.join(''),
    '\t\t<text xml:space="preserve" ',
    // When we use attribute font-family we must quote it if font name as numerals
    // see https://www.w3.org/TR/CSS2/fonts.html#font-family-prop
    // `To avoid mistakes in escaping, it is recommended to quote font
    // family names that contain white space, digits, or punctuation characters other than hyphens:`
    this.fontFamily
      ? 'font-family="' + `'${this.fontFamily.replace(/"/g, "'")}'` + '" '
      : '',
    this.fontSize ? 'font-size="' + this.fontSize + '" ' : '',
    this.fontStyle ? 'font-style="' + this.fontStyle + '" ' : '',
    this.fontWeight ? 'font-weight="' + this.fontWeight + '" ' : '',
    textDecoration ? 'text-decoration="' + textDecoration + '" ' : '',
    'style="',
    this.getSvgStyles(noShadow),
    '"',
    this.addPaintOrder(),
    ' >',
    textAndBg.textSpans.join(''),
    '</text>\n',
  ];
};

fabric.StaticCanvas.prototype.loadFromJSON = function(json, callback, reviver) {
  if (!json) {
    return;
  }

  // serialize if it wasn't already
  var serialized =
    typeof json === 'string'
      ? JSON.parse(json)
      : fabric.util.object.clone(json);

  var _this = this;

  // CUSTOM: keep a reference to the current renderOnAddRemove value, but only if there is
  // no other loading operation in progress (this flag is reset to undefined in the
  // enlivened callback below)
  if (typeof this.__currentRenderOnAddRemove === 'undefined') {
    this.__currentRenderOnAddRemove = this.renderOnAddRemove;
  }
  this.renderOnAddRemove = false;
  delete serialized.clipPath;

  // CUSTOM: we need a rendering id flag that we will increment during concurrent
  // calls to loadFromJSON (this allows us to cancel an enlivened callback
  // if it does not correspond to the latest loadFromJSON call
  if (typeof this.__loadFromJSONUid === 'undefined') {
    this.__loadFromJSONUid = 0;
  }
  const currentLoadingUid = ++this.__loadFromJSONUid;

  this._enlivenObjects(
    serialized.objects,
    function(enlivenedObjects) {
      // CUSTOM: cancel callback altogether if not last loadFromJSON call
      if (currentLoadingUid !== _this.__loadFromJSONUid) {
        return;
      }
      _this.clear();
      _this._setBgOverlay(serialized, function() {
        enlivenedObjects.forEach(function(obj, index) {
          if (
            !(
              obj.type === 'textbox' &&
              (obj.height === 0 || obj.width === 0)
            ) &&
            !obj.parentSymbol
          ) {
            // text blocks with 0 size can kill the editor and viewer
            // we splice the array just in case some custom classes restored from JSON
            // will add more object to canvas at canvas init.
            _this.insertAt(obj, index);
          }
        });
        // CUSTOM: reset renderOnAddRemove and temporary flags
        _this.renderOnAddRemove = _this.__currentRenderOnAddRemove;
        _this.__currentRenderOnAddRemove = undefined;
        _this.__loadFromJSONUid = 0;

        // remove parts i cannot set as options
        delete serialized.objects;
        delete serialized.backgroundImage;
        delete serialized.overlayImage;
        delete serialized.background;
        delete serialized.overlay;
        // this._initOptions does too many things to just
        // call it. Normally loading an Object from JSON
        // create the Object instance. Here the Canvas is
        // already an instance and we are just loading things over it
        _this._setOptions(serialized);
        _this.renderAll();

        if (callback) {
          callback();
        }
      });
    },
    reviver,
  );
  return this;
};

// override svg gradient for shapes non deformed
fabric.Gradient.prototype.toSVG = function(object, options) {
  options = options || {};

  var clone = fabric.util.object.clone;
  var coords = clone(this.coords, true);
  var i;
  var len;
  var markup;
  var commonAttributes;
  var colorStops = clone(this.colorStops, true);
  var needsSwap = coords.r1 > coords.r2;
  var transform = this.gradientTransform
    ? this.gradientTransform.concat()
    : fabric.iMatrix.concat();
  var offsetX = object.width / 2 - this.offsetX;
  var offsetY = object.height / 2 - this.offsetY;
  var withViewport = !!options.additionalTransform;
  // colorStops must be sorted ascending
  colorStops.sort(function(a, b) {
    return a.offset - b.offset;
  });

  if (object.type === 'path') {
    offsetX -= object.pathOffset.x;
    offsetY -= object.pathOffset.y;
  }
  if (object.type === 'ludusRect') {
    transform = [1, 0, 0, 1, 0, 0];
  } else {
    transform[4] -= offsetX;
    transform[5] -= offsetY;
  }

  commonAttributes =
    'id="SVGID_' + this.id + '" gradientUnits="userSpaceOnUse"';
  commonAttributes +=
    ' gradientTransform="' +
    (withViewport ? options.additionalTransform + ' ' : '') +
    fabric.util.matrixToSVG(transform) +
    '" ';

  if (this.type === 'linear') {
    markup = [
      '<linearGradient ',
      commonAttributes,
      ' x1="',
      coords.x1,
      '" y1="',
      coords.y1,
      '" x2="',
      coords.x2,
      '" y2="',
      coords.y2,
      '">\n',
    ];
  } else if (this.type === 'radial') {
    // svg radial gradient has just 1 radius. the biggest.
    markup = [
      '<radialGradient ',
      commonAttributes,
      ' cx="',
      needsSwap ? coords.x1 : coords.x2,
      '" cy="',
      needsSwap ? coords.y1 : coords.y2,
      '" r="',
      needsSwap ? coords.r1 : coords.r2,
      '" fx="',
      needsSwap ? coords.x2 : coords.x1,
      '" fy="',
      needsSwap ? coords.y2 : coords.y1,
      '">\n',
    ];
  }

  if (this.type === 'radial') {
    if (needsSwap) {
      // svg goes from internal to external radius. if radius are inverted, swap color stops.
      colorStops = colorStops.concat();
      colorStops.reverse();
      for (i = 0, len = colorStops.length; i < len; i++) {
        colorStops[i].offset = 1 - colorStops[i].offset;
      }
    }
    var minRadius = Math.min(coords.r1, coords.r2);
    if (minRadius > 0) {
      // i have to shift all colorStops and add new one in 0.
      var maxRadius = Math.max(coords.r1, coords.r2);
      var percentageShift = minRadius / maxRadius;
      for (i = 0, len = colorStops.length; i < len; i++) {
        colorStops[i].offset += percentageShift * (1 - colorStops[i].offset);
      }
    }
  }

  for (i = 0, len = colorStops.length; i < len; i++) {
    var colorStop = colorStops[i];
    markup.push(
      '<stop ',
      'offset="',
      colorStop.offset * 100 + '%',
      '" style="stop-color:',
      colorStop.color,
      typeof colorStop.opacity !== 'undefined'
        ? ';stop-opacity: ' + colorStop.opacity
        : ';',
      '"/>\n',
    );
  }

  markup.push(
    this.type === 'linear' ? '</linearGradient>\n' : '</radialGradient>\n',
  );

  return markup.join('');
};

fabric.Canvas.prototype._performTransformAction = function(
  e,
  transform,
  pointer,
) {
  var x = pointer.x;
  var y = pointer.y;
  var action = transform.action;
  var actionPerformed = false;
  var options = {
    target: transform.target,
    e: e,
    transform: transform,
    pointer: pointer,
  };

  if (action.name && action.name === 'custom') {
    transform.target.setFromHandle(action, x, y);
    actionPerformed = true;
  } else if (action === 'rotate') {
    (actionPerformed = this._rotateObject(x, y)) &&
      this._fire('rotating', options);
  } else if (action === 'scale') {
    (actionPerformed = this._onScale(e, transform, x, y)) &&
      this._fire('scaling', options);
  } else if (action === 'scaleX') {
    (actionPerformed = this._scaleObject(x, y, 'x')) &&
      this._fire('scaling', options);
  } else if (action === 'scaleY') {
    (actionPerformed = this._scaleObject(x, y, 'y')) &&
      this._fire('scaling', options);
  } else if (action === 'skewX') {
    (actionPerformed = this._skewObject(x, y, 'x')) &&
      this._fire('skewing', options);
  } else if (action === 'skewY') {
    (actionPerformed = this._skewObject(x, y, 'y')) &&
      this._fire('skewing', options);
  } else {
    actionPerformed = this._translateObject(x, y);
    if (actionPerformed) {
      this._fire('moving', options);
      this.setCursor(options.target.moveCursor || this.moveCursor);
    }
  }
  transform.actionPerformed = transform.actionPerformed || actionPerformed;
};

fabric.Canvas.prototype._getActionFromCorner = function(
  alreadySelected,
  corner,
  e /* target */,
) {
  if (!corner || !alreadySelected) {
    return 'drag';
  }
  if (typeof corner === 'object') {
    return corner;
  }

  switch (corner) {
    case 'p1':
    case 'p2':
      return 'moveHandle';
    case 'mtr':
      return 'rotate';
    case 'ml':
    case 'mr':
      return e[this.altActionKey] ? 'skewY' : 'scaleX';
    case 'mt':
    case 'mb':
      return e[this.altActionKey] ? 'skewX' : 'scaleY';
    default:
      return 'scale';
  }
};

// We check if object has non-deformed stroke enabled.
// if so we return adapated coord.
fabric.Object.prototype._getNonTransformedDimensions = function() {
  return {
    x: this.width,
    y: this.height,
  };
};
/* eslint-enable */

const onMouseDownOverridden = fabric.Canvas.prototype.__onMouseDown;
fabric.Canvas.prototype.__onMouseDown = function(e) {
  if (this.isSpaceDown) {
    return;
  }
  onMouseDownOverridden.call(this, e);
};
/*
 * Dispatch events when background is rendered
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.StaticCanvas.prototype._renderBackground = function(ctx) {
  // custom code start ------------------------------------------------------------------
  this.fire('before:background:render');
  // custom code end --------------------------------------------------------------------
  this._renderBackgroundOrOverlay(ctx, 'background');
  // custom code start ------------------------------------------------------------------
  this.fire('after:background:render');
  // custom code end --------------------------------------------------------------------
};

/*
 * Dispatch events when overlay is rendered
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.StaticCanvas.prototype._renderOverlay = function(ctx) {
  // custom code start ------------------------------------------------------------------
  this.fire('before:overlay:render');
  // custom code end --------------------------------------------------------------------
  this._renderBackgroundOrOverlay(ctx, 'overlay');
  // custom code start ------------------------------------------------------------------
  this.fire('after:overlay:render');
  // custom code end --------------------------------------------------------------------
};

/*
 * Getter for absolute, unzoomed canvas dimensions
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.StaticCanvas.prototype.getAbsoluteDimensions = function() {
  return new fabric.Point(this.getAbsoluteWidth(), this.getAbsoluteHeight());
};

/*
 * Getter for absolute, unzoomed canvas width
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.StaticCanvas.prototype.getAbsoluteWidth = function() {
  return this.vptCoords.br.x;
};

/*
 * Getter for absolute, unzoomed canvas height
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.StaticCanvas.prototype.getAbsoluteHeight = function() {
  return this.vptCoords.br.y;
};

/*
 * Getter for viewport center
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.StaticCanvas.prototype.getViewportCenter = function() {
  return { x: -this.viewportTransform[4], y: -this.viewportTransform[5] };
};

/*
 * Find an object type within a group
 *
 * TODO: fork Fabric and submit a PR
 */

fabric.StaticCanvas.prototype.allImagesRendered = function(target) {
  const images = fabric.util.getAllObjectsOfType(this, 'imageAsync', []);
  const charts = fabric.util.getAllObjectsOfType(this, 'ludusChart', []);
  if (!images.length && !charts.length) {
    return true;
  }

  const allrendered =
    images.every(o => o.rendered) && charts.every(o => o._objects[0].rendered);
  return allrendered;
};

fabric.util.getAllObjectsOfType = function(object, type, foundObjects = []) {
  if (object && object.type === type) {
    return [...foundObjects, object];
  } else if (object && object._objects) {
    object._objects.forEach(o => {
      foundObjects = fabric.util.getAllObjectsOfType(o, type, foundObjects);
    });
  }
  return [...foundObjects];
};

fabric.util.objectContainsType = function(object, types) {
  if (!object || !types || !types.length) {
    return false;
  }

  if (types.includes(object.type)) {
    return true;
  } else if (object._objects) {
    let contains = false;
    object._objects.forEach(o => {
      contains = contains || fabric.util.objectContainsType(o, types);
    });
    return contains;
  }
  return false;
};

/*
 * Find an object within a group
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.util.treeContainsObject = function(tree, object) {
  if (!tree || !object) {
    return false;
  }
  if (tree === object) {
    return true;
  } else if (tree instanceof fabric.Group) {
    let contains = false;
    tree.forEachObject(o => {
      contains = contains || fabric.util.treeContainsObject(o, object);
    });
    return contains;
  }
  return false;
};

/*
 * Returns the point in global coordinates
 *
 * TODO: fork Fabric and submit a PR
 * This method is oddly commented out in object_origin_mixin.js,
 * we'd just need to uncomment it
 */
fabric.util.object.extend(fabric.Object.prototype, {
  toGlobalPoint(point) {
    return fabric.util
      .rotatePoint(point, this.getCenterPoint(), degreesToRadians(this.angle))
      .addEquals(new fabric.Point(this.left, this.top));
  },
});

/*
 * Fixes Safari bug with out-of-bounds parameters for CanvasRenderingContext2D.drawImage()
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.Image.prototype._renderFill = function(ctx) {
  var x = -this.width / 2;
  var y = -this.height / 2;
  var elementToDraw = this._element;

  if (elementToDraw) {
    var cropX = Math.max(0, this.cropX);
    var cropY = Math.max(0, this.cropY);
    var negativeCropX = cropX - this.cropX;
    var negativeCropY = cropY - this.cropY;
    var width = Math.min(
      elementToDraw.width - cropX,
      this.width - negativeCropX,
    );
    var height = Math.min(
      elementToDraw.height - cropY,
      this.height - negativeCropY,
    );

    ctx.drawImage(
      elementToDraw,
      cropX,
      cropY,
      width,
      height,
      x + negativeCropX,
      y + negativeCropY,
      width,
      height,
    );
  }
};
/* eslint-enable */

/*
 * Fixes ungrouping so it keeps original flipX & flipY values
 *
 * TODO: fork Fabric and submit a PR
 */
fabric.Group.prototype.realizeTransform = function(object) {
  var matrix = object.calcTransformMatrix();
  var options = fabric.util.qrDecompose(matrix);
  var center = new fabric.Point(options.translateX, options.translateY);
  // custom code start ------------------------------------------------------------------
  var originalFlipX = object.flipX;
  var originalFlipY = object.flipY;
  // custom code end ------------------------------------------------------------------

  object.flipX = false;
  object.flipY = false;
  object.set('scaleX', options.scaleX);
  object.set('scaleY', options.scaleY);
  object.skewX = options.skewX;
  object.skewY = options.skewY;

  // custom code start ------------------------------------------------------------------
  if (originalFlipX !== object.flipX || originalFlipY !== object.flipY) {
    object.flipX = originalFlipX;
    object.flipY = originalFlipY;
    options.angle -= 180;
  }
  // custom code end ------------------------------------------------------------------

  object.angle = options.angle;
  object.setPositionByOrigin(center, 'center', 'center');

  return object;
};

// HACK to fix updateActiveSelection with shift+click on more than 2 objects: order is incorrect
// you can only see it when you group or copy paste that selection
fabric.Canvas.prototype._updateActiveSelection = function(target, e) {
  var activeSelection = this._activeObject;
  var currentActiveObjects = activeSelection._objects.slice(0);
  if (activeSelection.contains(target)) {
    activeSelection.removeWithUpdate(target);
    this._hoveredTarget = target;
    this._hoveredTargets = this.targets.concat();
    if (activeSelection.size() === 1) {
      // activate last remaining object
      this._setActiveObject(activeSelection.item(0), e);
    }
  } else {
    // we find objects from canvas so we can keep their order correct
    const newObjects = this._objects.filter(
      o =>
        o === target ||
        activeSelection._objects.find(selectedObject => selectedObject === o),
    );

    // we make sure no object is still considered as active selection
    newObjects.forEach(o => activeSelection.removeWithUpdate(o));
    // we create a new selection based on this array with correct order
    const newActiveSelection = new fabric.ActiveSelection(newObjects, {
      canvas: this,
    });
    activeSelection.destroy();
    this._setActiveObject(newActiveSelection, e);
    this._hoveredTarget = newActiveSelection;
    this._hoveredTargets = this.targets.concat();
  }
  this._fireSelectionEvents(currentActiveObjects, e);
};

export default fabric;
